import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { IoBoatOutline, IoClose } from "react-icons/io5";
import {
  FaShip,
  FaUsers,
  FaRulerCombined,
  FaMoneyBillWave,
  FaMinusCircle,
  FaCheckCircle,
  FaTags,
  FaImage,
  FaInfoCircle,
  FaCog,
  FaTrash,
  FaSave,
  FaClock,
  FaEuroSign,
  FaUserFriends,
  FaBan,
} from "react-icons/fa"; // Added more icons
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import Swal from "sweetalert2";

// Constants
const YACHT_TYPES = [
  "Motor Yacht",
  "Sailing Yacht",
  "Catamaran",
  "Cruiser",
  "Rib Boat",
  "Caique",
];
const YACHT_STATUSES = ["available", "maintenance", "unavailable"];
const CRUISE_TYPES = [
  "Private Half Day",
  "Private Full Day",
  "Semi-Private Half Day",
  "Semi-Private Full Day",
];

// Default durations (can be adjusted or made dynamic if needed)
const DEFAULT_DURATIONS = {
  "Private Half Day": 4,
  "Private Full Day": 8,
  "Semi-Private Half Day": 4,
  "Semi-Private Full Day": 8,
};

// Helper to get image path
const getImagePath = (imagePath) => {
  if (!imagePath) return null; // Return null if no path initially
  if (imagePath.startsWith("/uploads/")) {
    const serverBase = (
      process.env.REACT_APP_API_URL || "https://yacht-server.create-site.gr"
    ).replace("/api", "");
    return `${serverBase}${imagePath}`;
  }
  if (imagePath.startsWith("http")) {
    return imagePath;
  }
  // If it's a blob URL (from preview)
  if (imagePath.startsWith("blob:")) {
    return imagePath;
  }
  return null;
};

// Update the createDefaultPricingStructure function to ensure all required keys are explicitly created
const createDefaultPricingStructure = () => {
  // Create an empty structure with the necessary season definition
  const pricing = {
    seasonDefinition: {
      low: "April, May, September, October",
      medium: "",
      high: "June, July, August",
    },
    // Explicitly define each cruise type key with all required nested properties
    privateHalfDay: {
      lowSeason: {
        fixedRate: "",
        perGuestRate: "",
        maxPrice: "",
        guestTiers: [],
      },
      mediumSeason: {
        fixedRate: "",
        perGuestRate: "",
        maxPrice: "",
        guestTiers: [],
      },
      highSeason: {
        fixedRate: "",
        perGuestRate: "",
        maxPrice: "",
        guestTiers: [],
      },
    },
    privateFullDay: {
      lowSeason: {
        fixedRate: "",
        perGuestRate: "",
        maxPrice: "",
        guestTiers: [],
      },
      mediumSeason: {
        fixedRate: "",
        perGuestRate: "",
        maxPrice: "",
        guestTiers: [],
      },
      highSeason: {
        fixedRate: "",
        perGuestRate: "",
        maxPrice: "",
        guestTiers: [],
      }, // Fixed missing fixedRate: value
    },
    semiPrivateHalfDay: {
      lowSeason: {
        fixedRate: "",
        perGuestRate: "",
        maxPrice: "",
        guestTiers: [],
      },
      mediumSeason: {
        fixedRate: "",
        perGuestRate: "",
        maxPrice: "",
        guestTiers: [],
      },
      highSeason: {
        fixedRate: "",
        perGuestRate: "",
        maxPrice: "",
        guestTiers: [],
      },
    },
    semiPrivateFullDay: {
      lowSeason: {
        fixedRate: "",
        perGuestRate: "",
        maxPrice: "",
        guestTiers: [],
      },
      mediumSeason: {
        fixedRate: "",
        perGuestRate: "",
        maxPrice: "",
        guestTiers: [],
      },
      highSeason: {
        fixedRate: "",
        perGuestRate: "",
        maxPrice: "",
        guestTiers: [],
      },
    },
  };

  // Make sure it's initialized correctly
  console.log(
    "Created default pricing structure with keys:",
    Object.keys(pricing)
  );
  return pricing;
};

// Update transformToUIFormat to be more robust and handle direct storage format
const transformToUIFormat = (serverPricing) => {
  console.log("Transforming pricing data to UI format:", serverPricing);

  // Start with the default structure to ensure all paths exist
  const uiFormat = createDefaultPricingStructure();

  if (!serverPricing) return uiFormat;

  try {
    // Add or override season definitions if they exist
    if (serverPricing.seasonDefinition) {
      uiFormat.seasonDefinition = {
        ...uiFormat.seasonDefinition, // Keep defaults
        ...serverPricing.seasonDefinition, // Override with custom values
      };
    }

    // Check if we already have UI format (this might happen if editing a previously saved yacht)
    // Look for the privateHalfDay, privateFullDay, etc. keys
    const possibleUiKeys = [
      "privateHalfDay",
      "privateFullDay",
      "semiPrivateHalfDay",
      "semiPrivateFullDay",
    ];
    const hasUiFormat = possibleUiKeys.some((key) => serverPricing[key]);

    if (hasUiFormat) {
      console.log("Detected existing UI format in pricing data");
      // Just copy the values from the existing data to our structure
      possibleUiKeys.forEach((key) => {
        if (serverPricing[key]) {
          ["lowSeason", "highSeason", "mediumSeason"].forEach((seasonKey) => {
            if (serverPricing[key][seasonKey]) {
              // Copy fields for this season
              uiFormat[key][seasonKey] = {
                ...uiFormat[key][seasonKey],
                ...serverPricing[key][seasonKey],
              };
            }
          });
        }
      });
    } else {
      // We have server format data (private/semiPrivate hierarchy)
      console.log("Processing server format pricing data");

      // Map private cruise options
      if (serverPricing.private) {
        // Handle Private Half Day
        if (serverPricing.private.halfDay) {
          processSeasonData(
            serverPricing.private.halfDay,
            uiFormat.privateHalfDay
          );
        }

        // Handle Private Full Day
        if (serverPricing.private.fullDay) {
          processSeasonData(
            serverPricing.private.fullDay,
            uiFormat.privateFullDay
          );
        }
      }

      // Map semi-private cruise options
      if (serverPricing.semiPrivate) {
        // Handle Semi-Private Half Day
        if (serverPricing.semiPrivate.halfDay) {
          processSeasonData(
            serverPricing.semiPrivate.halfDay,
            uiFormat.semiPrivateHalfDay
          );
        }

        // Handle Semi-Private Full Day
        if (serverPricing.semiPrivate.fullDay) {
          processSeasonData(
            serverPricing.semiPrivate.fullDay,
            uiFormat.semiPrivateFullDay
          );
        }
      }
    }

    // Helper function to process season data for each cruise type
    function processSeasonData(sourceSeasons, targetCruiseType) {
      // Process Low Season
      if (sourceSeasons.lowSeason) {
        Object.assign(targetCruiseType.lowSeason, {
          fixedRate: sourceSeasons.lowSeason.fixedRate ?? "",
          perGuestRate: sourceSeasons.lowSeason.perGuestRate ?? "",
          maxPrice: sourceSeasons.lowSeason.maxPrice ?? "",
          guestTiers: Array.isArray(sourceSeasons.lowSeason.guestTiers)
            ? [...sourceSeasons.lowSeason.guestTiers]
            : [],
        });
      }

      // Process High Season
      if (sourceSeasons.highSeason) {
        Object.assign(targetCruiseType.highSeason, {
          fixedRate: sourceSeasons.highSeason.fixedRate ?? "",
          perGuestRate: sourceSeasons.highSeason.perGuestRate ?? "",
          maxPrice: sourceSeasons.highSeason.maxPrice ?? "",
          guestTiers: Array.isArray(sourceSeasons.highSeason.guestTiers)
            ? [...sourceSeasons.highSeason.guestTiers]
            : [],
        });
      }

      // Process Medium Season
      if (sourceSeasons.mediumSeason) {
        Object.assign(targetCruiseType.mediumSeason, {
          fixedRate: sourceSeasons.mediumSeason.fixedRate ?? "",
          perGuestRate: sourceSeasons.mediumSeason.perGuestRate ?? "",
          maxPrice: sourceSeasons.mediumSeason.maxPrice ?? "",
          guestTiers: Array.isArray(sourceSeasons.mediumSeason.guestTiers)
            ? [...sourceSeasons.mediumSeason.guestTiers]
            : [],
        });
      }
    }

    // Log transformed structure to help with debugging
    console.log("Final transformed pricing structure:", uiFormat);

    // Log some sample values to verify
    const checkPaths = [
      "privateHalfDay.lowSeason.fixedRate",
      "privateHalfDay.lowSeason.perGuestRate",
      "semiPrivateHalfDay.lowSeason.perGuestRate",
    ];

    checkPaths.forEach((path) => {
      const parts = path.split(".");
      let value = uiFormat;
      for (const part of parts) {
        value = value?.[part];
      }
      console.log(`Value at path ${path}:`, value);
    });

    return uiFormat;
  } catch (error) {
    console.error("Error transforming pricing data:", error);
    return uiFormat; // Return already created structure on error
  }
};

const mergePricingData = (existingPricing) => {
  console.log("Starting mergePricingData with:", existingPricing);

  // Default structure with standard keys used in the UI
  const defaultStructure = createDefaultPricingStructure();

  // If no pricing data exists, just return the empty default structure
  if (!existingPricing || typeof existingPricing !== "object") {
    return defaultStructure;
  }

  // Create a deep copy of the default structure
  const merged = JSON.parse(JSON.stringify(defaultStructure));

  // Map server structure to UI format
  try {
    // Add season definitions first
    if (existingPricing.seasonDefinition) {
      merged.seasonDefinition = { ...existingPricing.seasonDefinition };
    }

    // Private Half Day
    if (existingPricing.private?.halfDay) {
      const targetKey = "privateHalfDay";

      // Low Season
      if (existingPricing.private.halfDay.lowSeason) {
        const sourceSeason = existingPricing.private.halfDay.lowSeason;
        merged[targetKey].lowSeason = {
          fixedRate:
            sourceSeason.fixedRate !== undefined ? sourceSeason.fixedRate : "",
          perGuestRate:
            sourceSeason.perGuestRate !== undefined
              ? sourceSeason.perGuestRate
              : "",
          maxPrice:
            sourceSeason.maxPrice !== undefined ? sourceSeason.maxPrice : "",
          guestTiers: Array.isArray(sourceSeason.guestTiers)
            ? [...sourceSeason.guestTiers]
            : [],
        };
      }

      // High Season
      if (existingPricing.private.halfDay.highSeason) {
        const sourceSeason = existingPricing.private.halfDay.highSeason;
        merged[targetKey].highSeason = {
          fixedRate:
            sourceSeason.fixedRate !== undefined ? sourceSeason.fixedRate : "",
          perGuestRate:
            sourceSeason.perGuestRate !== undefined
              ? sourceSeason.perGuestRate
              : "",
          maxPrice:
            sourceSeason.maxPrice !== undefined ? sourceSeason.maxPrice : "",
          guestTiers: Array.isArray(sourceSeason.guestTiers)
            ? [...sourceSeason.guestTiers]
            : [],
        };
      }

      // Medium Season
      if (existingPricing.private.halfDay.mediumSeason) {
        const sourceSeason = existingPricing.private.halfDay.mediumSeason;
        merged[targetKey].mediumSeason = {
          fixedRate:
            sourceSeason.fixedRate !== undefined ? sourceSeason.fixedRate : "",
          perGuestRate:
            sourceSeason.perGuestRate !== undefined
              ? sourceSeason.perGuestRate
              : "",
          maxPrice:
            sourceSeason.maxPrice !== undefined ? sourceSeason.maxPrice : "",
          guestTiers: Array.isArray(sourceSeason.guestTiers)
            ? [...sourceSeason.guestTiers]
            : [],
        };
      }
    }

    // Private Full Day
    if (existingPricing.private?.fullDay) {
      const targetKey = "privateFullDay";

      // Low Season
      if (existingPricing.private.fullDay.lowSeason) {
        const sourceSeason = existingPricing.private.fullDay.lowSeason;
        merged[targetKey].lowSeason = {
          fixedRate:
            sourceSeason.fixedRate !== undefined ? sourceSeason.fixedRate : "",
          perGuestRate:
            sourceSeason.perGuestRate !== undefined
              ? sourceSeason.perGuestRate
              : "",
          maxPrice:
            sourceSeason.maxPrice !== undefined ? sourceSeason.maxPrice : "",
          guestTiers: Array.isArray(sourceSeason.guestTiers)
            ? [...sourceSeason.guestTiers]
            : [],
        };
      }

      // High Season
      if (existingPricing.private.fullDay.highSeason) {
        const sourceSeason = existingPricing.private.fullDay.highSeason;
        merged[targetKey].highSeason = {
          fixedRate:
            sourceSeason.fixedRate !== undefined ? sourceSeason.fixedRate : "",
          perGuestRate:
            sourceSeason.perGuestRate !== undefined
              ? sourceSeason.perGuestRate
              : "",
          maxPrice:
            sourceSeason.maxPrice !== undefined ? sourceSeason.maxPrice : "",
          guestTiers: Array.isArray(sourceSeason.guestTiers)
            ? [...sourceSeason.guestTiers]
            : [],
        };
      }

      // Medium Season
      if (existingPricing.private.fullDay.mediumSeason) {
        const sourceSeason = existingPricing.private.fullDay.mediumSeason;
        merged[targetKey].mediumSeason = {
          fixedRate:
            sourceSeason.fixedRate !== undefined ? sourceSeason.fixedRate : "",
          perGuestRate:
            sourceSeason.perGuestRate !== undefined
              ? sourceSeason.perGuestRate
              : "",
          maxPrice:
            sourceSeason.maxPrice !== undefined ? sourceSeason.maxPrice : "",
          guestTiers: Array.isArray(sourceSeason.guestTiers)
            ? [...sourceSeason.guestTiers]
            : [],
        };
      }
    }

    // Semi-Private Half Day
    if (existingPricing.semiPrivate?.halfDay) {
      const targetKey = "semiPrivateHalfDay";

      // Low Season
      if (existingPricing.semiPrivate.halfDay.lowSeason) {
        const sourceSeason = existingPricing.semiPrivate.halfDay.lowSeason;
        merged[targetKey].lowSeason = {
          fixedRate:
            sourceSeason.fixedRate !== undefined ? sourceSeason.fixedRate : "",
          perGuestRate:
            sourceSeason.perGuestRate !== undefined
              ? sourceSeason.perGuestRate
              : "",
          maxPrice:
            sourceSeason.maxPrice !== undefined ? sourceSeason.maxPrice : "",
          guestTiers: Array.isArray(sourceSeason.guestTiers)
            ? [...sourceSeason.guestTiers]
            : [],
        };
      }

      // High Season
      if (existingPricing.semiPrivate.halfDay.highSeason) {
        const sourceSeason = existingPricing.semiPrivate.halfDay.highSeason;
        merged[targetKey].highSeason = {
          fixedRate:
            sourceSeason.fixedRate !== undefined ? sourceSeason.fixedRate : "",
          perGuestRate:
            sourceSeason.perGuestRate !== undefined
              ? sourceSeason.perGuestRate
              : "",
          maxPrice:
            sourceSeason.maxPrice !== undefined ? sourceSeason.maxPrice : "",
          guestTiers: Array.isArray(sourceSeason.guestTiers)
            ? [...sourceSeason.guestTiers]
            : [],
        };
      }

      // Medium Season
      if (existingPricing.semiPrivate.halfDay.mediumSeason) {
        const sourceSeason = existingPricing.semiPrivate.halfDay.mediumSeason;
        merged[targetKey].mediumSeason = {
          fixedRate:
            sourceSeason.fixedRate !== undefined ? sourceSeason.fixedRate : "",
          perGuestRate:
            sourceSeason.perGuestRate !== undefined
              ? sourceSeason.perGuestRate
              : "",
          maxPrice:
            sourceSeason.maxPrice !== undefined ? sourceSeason.maxPrice : "",
          guestTiers: Array.isArray(sourceSeason.guestTiers)
            ? [...sourceSeason.guestTiers]
            : [],
        };
      }
    }

    // Semi-Private Full Day
    if (existingPricing.semiPrivate?.fullDay) {
      const targetKey = "semiPrivateFullDay";

      // Low Season
      if (existingPricing.semiPrivate.fullDay.lowSeason) {
        const sourceSeason = existingPricing.semiPrivate.fullDay.lowSeason;
        merged[targetKey].lowSeason = {
          fixedRate:
            sourceSeason.fixedRate !== undefined ? sourceSeason.fixedRate : "",
          perGuestRate:
            sourceSeason.perGuestRate !== undefined
              ? sourceSeason.perGuestRate
              : "",
          maxPrice:
            sourceSeason.maxPrice !== undefined ? sourceSeason.maxPrice : "",
          guestTiers: Array.isArray(sourceSeason.guestTiers)
            ? [...sourceSeason.guestTiers]
            : [],
        };
      }

      // High Season
      if (existingPricing.semiPrivate.fullDay.highSeason) {
        const sourceSeason = existingPricing.semiPrivate.fullDay.highSeason;
        merged[targetKey].highSeason = {
          fixedRate:
            sourceSeason.fixedRate !== undefined ? sourceSeason.fixedRate : "",
          perGuestRate:
            sourceSeason.perGuestRate !== undefined
              ? sourceSeason.perGuestRate
              : "",
          maxPrice:
            sourceSeason.maxPrice !== undefined ? sourceSeason.maxPrice : "",
          guestTiers: Array.isArray(sourceSeason.guestTiers)
            ? [...sourceSeason.guestTiers]
            : [],
        };
      }

      // Medium Season
      if (existingPricing.semiPrivate.fullDay.mediumSeason) {
        const sourceSeason = existingPricing.semiPrivate.fullDay.mediumSeason;
        merged[targetKey].mediumSeason = {
          fixedRate:
            sourceSeason.fixedRate !== undefined ? sourceSeason.fixedRate : "",
          perGuestRate:
            sourceSeason.perGuestRate !== undefined
              ? sourceSeason.perGuestRate
              : "",
          maxPrice:
            sourceSeason.maxPrice !== undefined ? sourceSeason.maxPrice : "",
          guestTiers: Array.isArray(sourceSeason.guestTiers)
            ? [...sourceSeason.guestTiers]
            : [],
        };
      }
    }

    console.log("Merged pricing structure:", merged);
    return merged;
  } catch (error) {
    console.error("Error merging pricing data:", error);
    return defaultStructure; // Return default on error
  }
};

// Modify the transformToUIFormat function to ensure proper capitalization of keys

const YachtDetailsModal = ({
  yacht,
  isOpen,
  onClose,
  onSave,
  onDelete,
  isUpdating,
  isDeleting,
}) => {
  const [formData, setFormData] = useState(null);
  const [originalData, setOriginalData] = useState(null); // Store the original data for comparison
  const [hasChanges, setHasChanges] = useState(false); // Track whether changes have been made
  const [currentImages, setCurrentImages] = useState([]); // Existing image URLs/paths
  const [newImageFiles, setNewImageFiles] = useState([]); // New File objects
  const [imagePreviews, setImagePreviews] = useState([]); // URLs for preview (existing + new blobs)
  const [imagesToDelete, setImagesToDelete] = useState([]); // Paths of images marked for deletion
  const [editingTier, setEditingTier] = useState({
    cruiseKey: null,
    season: null,
    index: null,
  }); // Track which tier is being edited

  // Function to check if the form data has changed
  const checkForChanges = useCallback(() => {
    if (!formData || !originalData) return false;

    // Check if there are new images or images marked for deletion
    if (newImageFiles.length > 0 || imagesToDelete.length > 0) {
      return true;
    }

    // Perform a deep comparison of the form data and original data
    // Exclude certain fields that might have different representations but same value
    // like dates, or arrays vs. string conversions
    const formDataForComparison = { ...formData };
    const originalDataForComparison = { ...originalData };

    // Convert arrays to strings for comparison if needed
    [
      "includedItems",
      "signatureMealDetails",
      "extraServices",
      "destinations",
    ].forEach((field) => {
      if (Array.isArray(originalDataForComparison[field])) {
        originalDataForComparison[field] = originalDataForComparison[field].join(
          "\\n"
        );
      }
      if (Array.isArray(formDataForComparison[field])) {
        formDataForComparison[field] = formDataForComparison[field].join("\\n");
      }
    });

    // Convert to JSON and compare
    const formDataJson = JSON.stringify(formDataForComparison);
    const originalDataJson = JSON.stringify(originalDataForComparison);

    return formDataJson !== originalDataJson;
  }, [formData, originalData, newImageFiles, imagesToDelete]);

  // Effect to update hasChanges when relevant dependencies change
  useEffect(() => {
    if (formData && originalData) {
      const changed = checkForChanges();
      setHasChanges(changed);
    }
  }, [formData, originalData, newImageFiles, imagesToDelete, checkForChanges]);

  // Effect to initialize/reset form data when yacht or isOpen changes
  useEffect(() => {
    if (isOpen && yacht) {
      console.log("Initializing form data with yacht:", yacht);

      // Initialize cruise options, ensuring all types are present
      const initialCruiseOptions = CRUISE_TYPES.map((type) => {
        // Find if this option exists in yacht data
        const existingOption = yacht.cruiseOptions?.find(
          (o) => o.type === type
        );

        // If there is an option defined but no available flag, assume it's available
        const isAvailable = existingOption
          ? existingOption.available !== undefined
            ? existingOption.available
            : true
          : false;

        console.log(`Cruise option ${type}:`, {
          foundExistingOption: Boolean(existingOption),
          availableFlag: existingOption?.available,
          finalAvailability: isAvailable,
          durationHours:
            existingOption?.durationHours || DEFAULT_DURATIONS[type] || "",
        });

        // Return the cruise option with proper availability
        return {
          type,
          available: isAvailable,
          durationHours:
            existingOption?.durationHours || DEFAULT_DURATIONS[type] || "",
        };
      });

      // Initialize pricing, using the direct transformation instead
      console.log("Original pricing data:", yacht.pricing);
      const initialPricing = transformToUIFormat(yacht.pricing);
      console.log("Initial pricing for form:", initialPricing);

      const newFormData = {
        ...yacht, // Spread existing yacht data
        // Ensure specific fields are correctly formatted
        guestCapacity: yacht.guestCapacity ?? "",
        crewCapacity: yacht.crewCapacity ?? "",
        yearBuilt: yacht.yearBuilt ?? "",
        yearRefitted: yacht.yearRefitted ?? "",
        totalUnits: yacht.totalUnits ?? 1,
        specifications: {
          length: yacht.specifications?.length ?? "",
          beam: yacht.specifications?.beam ?? "",
          draft: yacht.specifications?.draft ?? "",
        },
        cruiseOptions: initialCruiseOptions,
        pricing: initialPricing,
        // Convert arrays back to newline-separated strings for textareas
        includedItems: Array.isArray(yacht.includedItems)
          ? yacht.includedItems.join("\\n")
          : "",
        signatureMealDetails: Array.isArray(yacht.signatureMealDetails)
          ? yacht.signatureMealDetails.join("\\n")
          : "",
        extraServices: Array.isArray(yacht.extraServices)
          ? yacht.extraServices.join("\\n")
          : "",
        destinations: {
          // Assuming simple structure
          primary: yacht.destinations?.primary ?? "",
          secondary: yacht.destinations?.secondary ?? "",
        },
        description: yacht.description ?? "",
        status: yacht.status ?? YACHT_STATUSES[0],
        // Images are handled separately below
      };

      // Log final cruise options to verify correct format
      console.log("Final cruise options:", newFormData.cruiseOptions);

      setFormData(newFormData);
      setOriginalData(JSON.parse(JSON.stringify(newFormData))); // Deep clone for comparison
      setHasChanges(false); // Reset changes flag when opening modal

      // Image handling
      const existingImagePaths = yacht.images || [];
      setCurrentImages(existingImagePaths);
      setImagePreviews(
        existingImagePaths.map((imgPath) => getImagePath(imgPath))
      ); // Generate initial previews
      setNewImageFiles([]); // Reset new files
      setImagesToDelete([]); // Reset deletions
    } else if (!isOpen) {
      // Clean up previews when modal closes
      imagePreviews.forEach((url) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
      setFormData(null); // Clear form data
      setOriginalData(null); // Clear original data
      setHasChanges(false); // Reset changes flag
      setCurrentImages([]);
      setNewImageFiles([]);
      setImagePreviews([]);
      setImagesToDelete([]);
    }
  }, [isOpen, yacht]); // Rerun effect if isOpen or yacht changes

  // Cleanup blob URLs on unmount
  useEffect(() => {
    return () => {
      imagePreviews.forEach((url) => {
        if (url.startsWith("blob:")) {
          URL.revokeObjectURL(url);
        }
      });
    };
  }, [imagePreviews]);

  if (!isOpen || !formData) return null;

  // Animation variants
  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: 20 },
    transition: { duration: 0.3 },
  };

  // --- Handlers ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleNestedChange = (e, parentKey) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [parentKey]: {
        ...prev[parentKey],
        [name]: value,
      },
    }));
  };

  // Handle pricing changes - updated for nested structure
  const handlePricingChange = (e, cruiseKey, seasonKey, fieldName) => {
    const { value } = e.target;
    setFormData((prev) => {
      // Deep copy to avoid mutation issues
      const newPricing = JSON.parse(JSON.stringify(prev.pricing));
      // Ensure path exists
      if (!newPricing[cruiseKey]) newPricing[cruiseKey] = {};
      if (!newPricing[cruiseKey][seasonKey])
        newPricing[cruiseKey][seasonKey] = {};

      newPricing[cruiseKey][seasonKey][fieldName] = value;

      return {
        ...prev,
        pricing: newPricing,
      };
    });
  };

  // Add a new tier to the guest tiers array
  const handleAddGuestTier = (cruiseKey, seasonKey) => {
    setFormData((prev) => {
      // Deep copy to avoid mutation issues
      const newPricing = JSON.parse(JSON.stringify(prev.pricing));
      if (!newPricing[cruiseKey]) {
        newPricing[cruiseKey] = {};
      }
      if (!newPricing[cruiseKey][seasonKey]) {
        newPricing[cruiseKey][seasonKey] = {};
      }
      if (!newPricing[cruiseKey][seasonKey].guestTiers) {
        newPricing[cruiseKey][seasonKey].guestTiers = [];
      }

      // Add a new tier
      newPricing[cruiseKey][seasonKey].guestTiers.push({
        guests: "",
        rate: "",
      });

      return {
        ...prev,
        pricing: newPricing,
      };
    });
  };

  // Remove a tier from the guest tiers array
  const handleRemoveGuestTier = (cruiseKey, seasonKey, tierIndex) => {
    setFormData((prev) => {
      // Deep copy to avoid mutation issues
      const newPricing = JSON.parse(JSON.stringify(prev.pricing));

      // Remove the tier at the specified index
      if (newPricing[cruiseKey]?.[seasonKey]?.guestTiers) {
        newPricing[cruiseKey][seasonKey].guestTiers.splice(tierIndex, 1);
      }

      return {
        ...prev,
        pricing: newPricing,
      };
    });
  };

  // Update a specific tier's value (either guests or rate)
  const handleGuestTierChange = (
    cruiseKey,
    seasonKey,
    tierIndex,
    field,
    value
  ) => {
    setFormData((prev) => {
      // Deep copy to avoid mutation issues
      const newPricing = JSON.parse(JSON.stringify(prev.pricing));

      // Update the specific field in the tier
      if (newPricing[cruiseKey]?.[seasonKey]?.guestTiers?.[tierIndex]) {
        newPricing[cruiseKey][seasonKey].guestTiers[tierIndex][field] = value;
      }

      return {
        ...prev,
        pricing: newPricing,
      };
    });
  };

  const handleCruiseOptionChange = (index, checked) => {
    setFormData((prev) => {
      const newCruiseOptions = [...prev.cruiseOptions];
      newCruiseOptions[index].available = checked;
      // Optionally clear duration if unchecked
      // if (!checked) {
      //   newCruiseOptions[index].durationHours = '';
      // }
      return { ...prev, cruiseOptions: newCruiseOptions };
    });
  };

  const handleCruiseDurationChange = (index, value) => {
    setFormData((prev) => {
      const newCruiseOptions = [...prev.cruiseOptions];
      newCruiseOptions[index].durationHours = value;
      return { ...prev, cruiseOptions: newCruiseOptions };
    });
  };

  // Handle image changes
  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setNewImageFiles((prev) => [...prev, ...files]);

      // Generate previews for new files
      const newPreviews = files.map((file) => URL.createObjectURL(file));
      setImagePreviews((prev) => [...prev, ...newPreviews]);

      // Set hasChanges to true since images have changed
      setHasChanges(true);
    }
    e.target.value = null; // Clear input
  };

  // Handle removing an image
  const handleRemoveImage = (indexToRemove) => {
    const previewUrlToRemove = imagePreviews[indexToRemove];

    // Check if it's an existing image or a new one
    const existingImageIndex = currentImages.findIndex(
      (imgPath) => getImagePath(imgPath) === previewUrlToRemove
    );

    if (existingImageIndex !== -1) {
      // It's an existing image, mark it for deletion
      const imagePathToRemove = currentImages[existingImageIndex];
      setImagesToDelete((prev) => [...prev, imagePathToRemove]);
      // Remove from currentImages state as well
      setCurrentImages((prev) =>
        prev.filter((_, index) => index !== existingImageIndex)
      );
      setHasChanges(true); // Set hasChanges to true
    } else {
      // It's a new image (blob URL), find its corresponding file
      const newFileIndex = imagePreviews
        .slice(currentImages.length) // Look only in the previews added after existing ones
        .findIndex((url) => url === previewUrlToRemove);

      if (newFileIndex !== -1) {
        // Remove the file from newImageFiles
        setNewImageFiles((prev) =>
          prev.filter((_, index) => index !== newFileIndex)
        );
        // Revoke the blob URL
        URL.revokeObjectURL(previewUrlToRemove);
      }
    }

    // Remove the preview regardless
    setImagePreviews((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  // Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();

    // Only proceed if there are actual changes
    if (!hasChanges) {
      console.log("No changes detected, skipping save operation");
      return; // Exit early if no changes
    }

    // Convert textarea strings to arrays
    const includedItemsArray = formData.includedItems
      .split("\\n")
      .map((item) => item.trim())
      .filter((item) => item);
    const signatureMealDetailsArray = formData.signatureMealDetails
      .split("\\n")
      .map((item) => item.trim())
      .filter((item) => item);
    const extraServicesArray = formData.extraServices
      .split("\\n")
      .map((item) => item.trim())
      .filter((item) => item);

    const dataToSubmit = new FormData();

    // Process yearBuilt and yearRefitted to ensure they're proper numbers or null
    const processedData = { ...formData };
    if (processedData.yearBuilt === "") {
      processedData.yearBuilt = null;
    } else if (processedData.yearBuilt) {
      processedData.yearBuilt = Number(processedData.yearBuilt);
    }

    if (processedData.yearRefitted === "") {
      processedData.yearRefitted = null;
    } else if (processedData.yearRefitted) {
      processedData.yearRefitted = Number(processedData.yearRefitted);
    }

    // Append simple fields that might have changed
    Object.keys(processedData).forEach((key) => {
      // Exclude fields handled separately or read-only
      if (
        ![
          "pricing",
          "specifications",
          "destinations",
          "cruiseOptions",
          "_id",
          "__v",
          "images",
          "availability",
          "includedItems",
          "signatureMealDetails",
          "extraServices",
          "stringId",
        ].includes(key)
      ) {
        // Only send if not undefined/null
        if (processedData[key] !== undefined && processedData[key] !== null) {
          dataToSubmit.append(key, processedData[key]);
        }
      }
    });

    // Append JSON stringified objects/arrays - ensure they're properly structured
    dataToSubmit.append(
      "specifications",
      JSON.stringify(formData.specifications || {})
    );
    dataToSubmit.append(
      "destinations",
      JSON.stringify(formData.destinations || {})
    );
    dataToSubmit.append(
      "cruiseOptions",
      JSON.stringify(formData.cruiseOptions || [])
    );
    dataToSubmit.append("pricing", JSON.stringify(formData.pricing || {})); // Send updated nested pricing
    dataToSubmit.append(
      "includedItems",
      JSON.stringify(includedItemsArray || [])
    );
    dataToSubmit.append(
      "signatureMealDetails",
      JSON.stringify(signatureMealDetailsArray || [])
    );
    dataToSubmit.append(
      "extraServices",
      JSON.stringify(extraServicesArray || [])
    );

    // Append new image files
    newImageFiles.forEach((file) => {
      dataToSubmit.append("yachtImages", file); // Match backend field name
    });

    // Append array of images to delete
    if (imagesToDelete.length > 0) {
      dataToSubmit.append("imagesToDelete", JSON.stringify(imagesToDelete));
    }

    // Log data being sent for debugging
    // console.log("Submitting FormData for update:");
    // for (let [key, value] of dataToSubmit.entries()) {
    //   console.log(key, value);
    // }

    onSave(yacht._id, dataToSubmit);
  };

  // Helper to generate cruise key (e.g., privateHalfDay)
  const getCruiseKey = (type) => {
    return type
      .toLowerCase() // Ensure first letter is lowercase
      .split(" ")
      .map((word, index) =>
        index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)
      )
      .join("")
      .replace(/-(\w)/g, (_, c) => c.toUpperCase());
  };
  const renderPricingValue = (cruiseKey, seasonKey, fieldName) => {
    return formData.pricing?.[cruiseKey]?.[seasonKey]?.[fieldName] || "";
  };

  // Add a debug function for displaying the entire state at render time
  const debugFormDataState = () => {
    console.log("FULL FORM DATA AT RENDER:", formData);
    console.log("PRICING DATA STRUCTURE:", formData?.pricing);
    if (formData?.pricing) {
      Object.keys(formData.pricing).forEach((key) => {
        if (
          key !== "seasonDefinition" &&
          typeof formData.pricing[key] === "object"
        ) {
          console.log(`PRICING FOR ${key}:`, formData.pricing[key]);
        }
      });
    }
  };

  // Function to handle editing a specific tier
  const handleEditTier = (cruiseKey, seasonKey, tierIndex) => {
    setEditingTier({ cruiseKey, season: seasonKey, index: tierIndex });
  };

  // Function to save tier edits and exit edit mode
  const handleSaveTierEdit = () => {
    setEditingTier({ cruiseKey: null, season: null, index: null });
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="bg-white rounded-xl shadow-2xl w-full max-w-5xl relative z-10 max-h-[95vh] flex flex-col" // Use flex column
          >
            {/* Header */}
            <div className="sticky top-0 z-20 bg-gradient-to-r from-gray-800 to-gray-700 px-6 py-4 border-b border-gray-600 flex justify-between items-center rounded-t-xl">
              <div className="flex items-center space-x-3">
                <IoBoatOutline className="text-white text-2xl" />
                <h2 className="text-xl font-bold text-white">
                  Edit Yacht Details
                </h2>
                <span className="text-xs text-gray-400">(ID: {yacht._id})</span>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-full text-gray-300 hover:bg-gray-600 hover:text-white transition-colors"
                aria-label="Close modal"
              >
                <IoClose size={24} />
              </button>
            </div>

            {/* Content - Scrollable */}
            <div className="p-6 overflow-y-auto flex-grow">
              {/* Assign ID to form for linking submit button */}
              <form
                id="yacht-details-form"
                onSubmit={handleSubmit}
                className="space-y-8"
              >
                {/* Yacht Identity Section */}
                <motion.section
                  {...fadeInUp}
                  className="bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm"
                >
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <FaShip className="mr-2 text-blue-600" />
                    Yacht Identity
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    {/* String ID (Readonly usually) */}
                    <div>
                      <label className="block text-sm font-medium text-gray-500">
                        String ID
                      </label>
                      <input
                        type="text"
                        value={formData.stringId}
                        readOnly
                        className="mt-1 block w-full rounded-md border-gray-300 bg-gray-100 shadow-sm text-gray-500 cursor-not-allowed"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Yacht Name *
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Builder *
                      </label>
                      <input
                        type="text"
                        name="builder"
                        value={formData.builder}
                        onChange={handleChange}
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Yacht Type *
                      </label>
                      <select
                        name="yachtType"
                        value={formData.yachtType}
                        onChange={handleChange}
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      >
                        {YACHT_TYPES.map((type) => (
                          <option key={type} value={type}>
                            {type}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Year Built
                      </label>
                      <input
                        type="number"
                        name="yearBuilt"
                        value={formData.yearBuilt || ""}
                        onChange={handleChange}
                        min="1900"
                        max={new Date().getFullYear() + 1}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Year Refitted
                      </label>
                      <input
                        type="number"
                        name="yearRefitted"
                        value={formData.yearRefitted || ""}
                        onChange={handleChange}
                        min="1900"
                        max={new Date().getFullYear() + 1}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </motion.section>

                {/* Capacity & Specifications Section */}
                <motion.section
                  {...fadeInUp}
                  className="bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm"
                >
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <FaRulerCombined className="mr-2 text-purple-600" />
                    Capacity & Specifications
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Guest Capacity *
                      </label>
                      <input
                        type="number"
                        name="guestCapacity"
                        value={formData.guestCapacity}
                        onChange={handleChange}
                        required
                        min="1"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Crew Capacity
                      </label>
                      <input
                        type="number"
                        name="crewCapacity"
                        value={formData.crewCapacity || ""}
                        onChange={handleChange}
                        min="0"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Total Units
                      </label>
                      <input
                        type="number"
                        name="totalUnits"
                        value={formData.totalUnits}
                        onChange={handleChange}
                        min="0"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Length (e.g., 18m) *
                      </label>
                      <input
                        type="text"
                        name="length"
                        value={formData.specifications.length}
                        onChange={(e) =>
                          handleNestedChange(e, "specifications")
                        }
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Beam (e.g., 5m) *
                      </label>
                      <input
                        type="text"
                        name="beam"
                        value={formData.specifications.beam}
                        onChange={(e) =>
                          handleNestedChange(e, "specifications")
                        }
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Draft (e.g., 1.5m) *
                      </label>
                      <input
                        type="text"
                        name="draft"
                        value={formData.specifications.draft}
                        onChange={(e) =>
                          handleNestedChange(e, "specifications")
                        }
                        required
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                </motion.section>

                {/* Cruise Options, Durations & Pricing */}
                <motion.section
                  {...fadeInUp}
                  className="bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm"
                >
                  <h3 className="text-lg font-semibold text-gray-800 mb-6 flex items-center">
                    <FaMoneyBillWave className="mr-2 text-green-600" />
                    Cruise Options, Durations & Pricing
                  </h3>

                  {/* Cruise Types & Durations */}
                  <div className="mb-8">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Available Cruise Types & Durations *
                    </label>
                    <div className="space-y-4">
                      {formData.cruiseOptions.map((option, index) => (
                        <div
                          key={option.type}
                          className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-center p-3 border rounded-md hover:bg-gray-100 transition-colors"
                        >
                          <label className="flex items-center space-x-3 cursor-pointer col-span-1 sm:col-span-1">
                            <input
                              type="checkbox"
                              checked={option.available}
                              onChange={(e) =>
                                handleCruiseOptionChange(
                                  index,
                                  e.target.checked
                                )
                              }
                              className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4"
                            />
                            <span className="text-sm font-medium text-gray-800">
                              {option.type}
                            </span>
                            {/* Add debug span temporarily - remove in production */}
                            <span className="text-xs text-gray-400">
                              (available: {option.available ? "true" : "false"})
                            </span>
                          </label>
                          <div className="col-span-1 sm:col-span-2">
                            <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center">
                              <FaClock className="mr-1 h-3 w-3 text-gray-500" />{" "}
                              Duration (Hours) *
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={option.durationHours ?? ""}
                              onChange={(e) =>
                                handleCruiseDurationChange(
                                  index,
                                  e.target.value
                                )
                              }
                              required={option.available}
                              disabled={!option.available}
                              className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm p-2 ${
                                !option.available
                                  ? "bg-gray-100 cursor-not-allowed text-gray-500"
                                  : ""
                              }`}
                              placeholder="e.g., 4"
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                    {formData.cruiseOptions.filter((o) => o?.available)
                      .length === 0 && (
                      <p className="text-red-500 text-xs mt-2">
                        At least one cruise type must be available and have a
                        duration.
                      </p>
                    )}
                  </div>

                  {/* Pricing Section */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Seasonal Pricing *
                    </label>
                    <p className="text-xs text-gray-500 mb-4">
                      Define pricing for each available cruise type per season.
                      Use 'Fixed Rate' OR 'Per Guest Rate' (with optional 'Max
                      Price' cap). 'Per Guest Rate' takes priority if both are
                      set.
                    </p>
                    <div className="space-y-6">
                      {formData.cruiseOptions
                        .filter((o) => o.available)
                        .map((option) => {
                          // Determine the cruise key mapping
                          const cruiseType = option.type.toLowerCase();
                          let pricingKey, dayType;

                          // Map the cruise option to pricing structure keys
                          if (cruiseType.includes("private")) {
                            pricingKey = "private";
                            if (cruiseType.includes("half day")) {
                              dayType = "halfDay";
                            } else if (cruiseType.includes("full day")) {
                              dayType = "fullDay";
                            }
                          } else if (cruiseType.includes("semi-private")) {
                            pricingKey = "semiPrivate";
                            if (cruiseType.includes("half day")) {
                              dayType = "halfDay";
                            } else if (cruiseType.includes("full day")) {
                              dayType = "fullDay";
                            }
                          }

                          const cruiseKey = getCruiseKey(option.type); // e.g., privateHalfDay

                          return (
                            <div
                              key={cruiseKey}
                              className="p-4 border rounded-md bg-white"
                            >
                              <h4 className="text-md font-semibold text-gray-700 mb-4">
                                {option.type}
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-x-6 gap-y-4">
                                {/* Headers */}
                                <div className="font-medium text-sm text-gray-500">
                                  Season
                                </div>
                                <div className="font-medium text-sm text-gray-500 text-center">
                                  Low (
                                  {formData.pricing?.seasonDefinition?.low ||
                                    "Apr, May, Sep, Oct"}
                                  )
                                </div>
                                <div className="font-medium text-sm text-gray-500 text-center">
                                  High (
                                  {formData.pricing?.seasonDefinition?.high ||
                                    "Jun, Jul, Aug"}
                                  )
                                </div>

                                {/* Fixed Rate Row */}
                                <div className="text-sm self-center flex items-center">
                                  <FaEuroSign className="mr-1 h-3 w-3 text-gray-500" />{" "}
                                  Fixed Rate
                                </div>
                                {["lowSeason", "highSeason"].map(
                                  (seasonKey) => (
                                    <input
                                      key={`${cruiseKey}-${seasonKey}-fixed`}
                                      type="number"
                                      min="0"
                                      step="any"
                                      // Use optional chaining and nullish coalescing
                                      value={
                                        formData.pricing?.[cruiseKey]?.[
                                          seasonKey
                                        ]?.fixedRate ?? ""
                                      }
                                      onChange={(e) =>
                                        handlePricingChange(
                                          e,
                                          cruiseKey,
                                          seasonKey,
                                          "fixedRate"
                                        )
                                      }
                                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm p-2"
                                      placeholder="e.g., 1000"
                                    />
                                  )
                                )}

{/* PER GUEST RATE */}
                                <div className="text-sm self-center flex items-center">
                                  <FaUserFriends className="mr-1 h-3 w-3 text-gray-500" />{" "}
                                  Per Guest Rate
                                </div>
                                {["lowSeason", "highSeason"].map(
                                  (seasonKey) => (
                                    <input
                                      key={`${cruiseKey}-${seasonKey}-perGuest`}
                                      type="number"
                                      min="0"
                                      step="any"
                                      value={
                                        formData.pricing?.[cruiseKey]?.[
                                          seasonKey
                                        ]?.perGuestRate ?? ""
                                      }
                                      onChange={(e) =>
                                        handlePricingChange(
                                          e,
                                          cruiseKey,
                                          seasonKey,
                                          "perGuestRate"
                                        )
                                      }
                                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm p-2"
                                      placeholder="e.g., 150"
                                    />
                                  )
                                )}

{/* MAX PRICE */}
                                <div className="text-sm self-center flex items-center">
                                  <FaBan className="mr-1 h-3 w-3 text-gray-500" />{" "}
                                  Max Price (Cap)
                                </div>
                                {["lowSeason", "highSeason"].map(
                                  (seasonKey) => {
                                    // Check if the max price is set for the current season
                                    const maxPriceSet =
                                      formData.pricing?.[cruiseKey]?.[
                                        seasonKey
                                      ]?.maxPrice !== undefined;
                                    const maxPriceValue =
                                      formData.pricing?.[cruiseKey]?.[
                                        seasonKey
                                      ]?.maxPrice ?? "";

                                    const isDisabled = !maxPriceSet; // Disable if not set
                                    return (
                                      <input
                                        key={`${cruiseKey}-${seasonKey}-max`}
                                        type="number"
                                        min="0"
                                        step="any"
                                        value={maxPriceValue}
                                        onChange={(e) =>
                                          handlePricingChange(
                                            e,
                                            cruiseKey,
                                            seasonKey,
                                            "maxPrice"
                                          )
                                        }
                                        disabled={isDisabled}
                                        className={`mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 text-sm p-2 ${
                                          isDisabled
                                            ? "bg-gray-100 cursor-not-allowed text-gray-500"
                                            : ""
                                        }`}
                                        placeholder="Optional cap"
                                      />
                                    );
                                  }
                                )}

                                {/* Guest Tier Pricing Section */}
                                <div className="col-span-3 mt-4">
                                  <div className="border-t pt-4 mb-2">
                                    <div className="flex justify-between items-center">
                                      <h5 className="text-sm font-medium text-gray-700">
                                        Guest Tier Pricing
                                      </h5>
                                      <p className="text-xs text-gray-500">
                                        For different prices based on number of
                                        guests
                                      </p>
                                    </div>
                                  </div>

                                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                                    {/* Low Season Tiers Column */}
                                    <div className="border rounded-md p-3">
                                      <div className="flex justify-between items-center mb-3">
                                        <h6 className="text-sm font-medium text-gray-700">
                                          Low Season Tiers
                                        </h6>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleAddGuestTier(
                                              cruiseKey,
                                              "lowSeason"
                                            )
                                          }
                                          className="px-2 py-1 bg-blue-50 text-blue-600 rounded-md text-xs hover:bg-blue-100"
                                        >
                                          + Add Tier
                                        </button>
                                      </div>
                                      {/* Low Season Tier Items - Display as elements, not inputs */}
                                      {(() => {
                                        // Find guest tiers using multiple possible paths
                                        let tiers =
                                          formData.pricing?.[cruiseKey]
                                            ?.lowSeason?.guestTiers || [];

                                        console.log(
                                          `Rendering ${
                                            tiers?.length || 0
                                          } tiers for ${cruiseKey} lowSeason:`,
                                          tiers
                                        );

                                        if (!tiers || tiers.length === 0) {
                                          return (
                                            <div className="text-center py-3">
                                              <p className="text-xs text-gray-500 italic">
                                                No tiers added. Click "Add Tier"
                                                to create guest-based pricing.
                                              </p>
                                            </div>
                                          );
                                        }

                                        return (
                                          <div className="space-y-2">
                                            {tiers.map((tier, tierIndex) => (
                                              <div
                                                key={tierIndex}
                                                className="flex justify-between items-center bg-gray-50 p-2 rounded-md border border-gray-200"
                                              >
                                                {editingTier.cruiseKey ===
                                                  cruiseKey &&
                                                editingTier.season ===
                                                  "lowSeason" &&
                                                editingTier.index ===
                                                  tierIndex ? (
                                                  // Edit mode
                                                  <div className="flex flex-1 space-x-2">
                                                    {/* Guests Input */}
                                                    <div className="flex-1">
                                                      <input
                                                        type="text"
                                                        value={
                                                          tier.guests || ""
                                                        }
                                                        onChange={(e) =>
                                                          handleGuestTierChange(
                                                            cruiseKey,
                                                            "lowSeason",
                                                            tierIndex,
                                                            "guests",
                                                            e.target.value
                                                          )
                                                        }
                                                        className="w-full text-sm border-gray-300 rounded-md shadow-sm"
                                                      />
                                                    </div>

                                                    {/* Rate Input */}
                                                    <div className="flex-1">
                                                      <div className="relative rounded-md shadow-sm">
                                                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                                                          <span className="text-gray-500 sm:text-sm">
                                                            €
                                                          </span>
                                                        </div>
                                                        <input
                                                          type="number"
                                                          min="0"
                                                          value={
                                                            tier.rate || ""
                                                          }
                                                          onChange={(e) =>
                                                            handleGuestTierChange(
                                                              cruiseKey,
                                                              "lowSeason",
                                                              tierIndex,
                                                              "rate",
                                                              e.target.value
                                                            )
                                                          }
                                                          className="w-full pl-6 text-sm border-gray-300 rounded-md shadow-sm"
                                                        />
                                                      </div>
                                                    </div>
                                                  </div>
                                                ) : (
                                                  // Display mode
                                                  <>
                                                    <div className="flex items-center space-x-3">
                                                      <span className="text-sm font-medium text-gray-700">
                                                        {tier.guests || "Any"}{" "}
                                                        guests:
                                                      </span>
                                                      <span className="text-sm font-bold text-blue-700">
                                                        €{tier.rate || "0"}
                                                      </span>
                                                    </div>
                                                    <div className="flex space-x-1">
                                                      <button
                                                        type="button"
                                                        onClick={() =>
                                                          handleEditTier(
                                                            cruiseKey,
                                                            "lowSeason",
                                                            tierIndex
                                                          )
                                                        }
                                                        className="p-1 text-gray-500 hover:text-blue-600"
                                                        title="Edit tier"
                                                      >
                                                        <svg
                                                          xmlns="http://www.w3.org/2000/svg"
                                                          className="h-4 w-4"
                                                          viewBox="0 0 20 20"
                                                          fill="currentColor"
                                                        >
                                                          <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                                                          <path
                                                            fillRule="evenodd"
                                                            d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
                                                            clipRule="evenodd"
                                                          />
                                                        </svg>
                                                      </button>
                                                      <button
                                                        type="button"
                                                        onClick={() =>
                                                          handleRemoveGuestTier(
                                                            cruiseKey,
                                                            "lowSeason",
                                                            tierIndex
                                                          )
                                                        }
                                                        className="p-1 text-gray-500 hover:text-red-600"
                                                        title="Remove tier"
                                                      >
                                                        <svg
                                                          xmlns="http://www.w3.org/2000/svg"
                                                          className="h-4 w-4"
                                                          viewBox="0 0 20 20"
                                                          fill="currentColor"
                                                        >
                                                          <path
                                                            fillRule="evenodd"
                                                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                            clipRule="evenodd"
                                                          />
                                                        </svg>
                                                      </button>
                                                    </div>
                                                  </>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        );
                                      })()}
                                    </div>

                                    {/* High Season Tiers Column */}
                                    <div className="border rounded-md p-3">
                                      <div className="flex justify-between items-center mb-3">
                                        <h6 className="text-sm font-medium text-gray-700">
                                          High Season Tiers
                                        </h6>
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleAddGuestTier(
                                              cruiseKey,
                                              "highSeason"
                                            )
                                          }
                                          className="px-2 py-1 bg-blue-50 text-blue-600 rounded-md text-xs hover:bg-blue-100"
                                        >
                                          + Add Tier
                                        </button>
                                      </div>
                                      {/* High Season Tier Items - Display as elements, not inputs */}
                                      {(() => {
                                        // Find guest tiers using multiple possible paths
                                        let tiers =
                                          formData.pricing?.[cruiseKey]
                                            ?.highSeason?.guestTiers || [];

                                        console.log(
                                          `Rendering ${
                                            tiers?.length || 0
                                          } tiers for ${cruiseKey} highSeason:`,
                                          tiers
                                        );

                                        if (!tiers || tiers.length === 0) {
                                          return (
                                            <div className="text-center py-3">
                                              <p className="text-xs text-gray-500 italic">
                                                No tiers added. Click "Add Tier"
                                                to create guest-based pricing.
                                              </p>
                                            </div>
                                          );
                                        }

                                        return (
                                          <div className="space-y-2">
                                            {tiers.map((tier, tierIndex) => (
                                              <div
                                                key={tierIndex}
                                                className="flex justify-between items-center bg-gray-50 p-2 rounded-md border border-gray-200"
                                              >
                                                {editingTier.cruiseKey ===
                                                  cruiseKey &&
                                                editingTier.season ===
                                                  "highSeason" &&
                                                editingTier.index ===
                                                  tierIndex ? (
                                                  // Edit mode
                                                  <div className="flex flex-1 space-x-2">
                                                    {/* Guests Input */}
                                                    <div className="flex-1">
                                                      <input
                                                        type="text"
                                                        value={
                                                          tier.guests || ""
                                                        }
                                                        onChange={(e) =>
                                                          handleGuestTierChange(
                                                            cruiseKey,
                                                            "lowSeason",
                                                            tierIndex,
                                                            "guests",
                                                            e.target.value
                                                          )
                                                        }
                                                        className="w-full text-sm border-gray-300 rounded-md shadow-sm"
                                                      />
                                                    </div>

                                                    {/* Rate Input */}
                                                    <div className="flex-1">
                                                      <div className="relative rounded-md shadow-sm">
                                                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2">
                                                          <span className="text-gray-500 sm:text-sm">
                                                            €
                                                          </span>
                                                        </div>
                                                        <input
                                                          type="number"
                                                          min="0"
                                                          value={
                                                            tier.rate || ""
                                                          }
                                                          onChange={(e) =>
                                                            handleGuestTierChange(
                                                              cruiseKey,
                                                              "lowSeason",
                                                              tierIndex,
                                                              "rate",
                                                              e.target.value
                                                            )
                                                          }
                                                          className="w-full pl-6 text-sm border-gray-300 rounded-md shadow-sm"
                                                        />
                                                      </div>
                                                    </div>
                                                  </div>
                                                ) : (
                                                  // Display mode
                                                  <>
                                                    <div className="flex items-center space-x-3">
                                                      <span className="text-sm font-medium text-gray-700">
                                                        {tier.guests || "Any"}{" "}
                                                        guests:
                                                      </span>
                                                      <span className="text-sm font-bold text-blue-700">
                                                        €{tier.rate || "0"}
                                                      </span>
                                                    </div>
                                                    <div className="flex space-x-1">
                                                      <button
                                                        type="button"
                                                        onClick={() =>
                                                          handleEditTier(
                                                            cruiseKey,
                                                            "highSeason",
                                                            tierIndex
                                                          )
                                                        }
                                                        className="p-1 text-gray-500 hover:text-blue-600"
                                                        title="Edit tier"
                                                      >
                                                        <svg
                                                          xmlns="http://www.w3.org/2000/svg"
                                                          className="h-4 w-4"
                                                          viewBox="0 0 20 20"
                                                          fill="currentColor"
                                                        >
                                                          <path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z" />
                                                          <path
                                                            fillRule="evenodd"
                                                            d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z"
                                                            clipRule="evenodd"
                                                          />
                                                        </svg>
                                                      </button>
                                                      <button
                                                        type="button"
                                                        onClick={() =>
                                                          handleRemoveGuestTier(
                                                            cruiseKey,
                                                            "highSeason",
                                                            tierIndex
                                                          )
                                                        }
                                                        className="p-1 text-gray-500 hover:text-red-600"
                                                        title="Remove tier"
                                                      >
                                                        <svg
                                                          xmlns="http://www.w3.org/2000/svg"
                                                          className="h-4 w-4"
                                                          viewBox="0 0 20 20"
                                                          fill="currentColor"
                                                        >
                                                          <path
                                                            fillRule="evenodd"
                                                            d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                                                            clipRule="evenodd"
                                                          />
                                                        </svg>
                                                      </button>
                                                    </div>
                                                  </>
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        );
                                      })()}
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      {/* Validation message */}
                      {formData.cruiseOptions.some(
                        (opt) =>
                          opt.available &&
                          !Object.values(
                            formData.pricing?.[getCruiseKey(opt.type)] || {}
                          ).some(
                            (season) =>
                              season?.fixedRate || season?.perGuestRate
                          )
                      ) && (
                        <p className="text-red-500 text-xs mt-2">
                          Pricing must be defined (Fixed or Per Guest) for all
                          available cruise types in at least one season.
                        </p>
                      )}
                    </div>
                  </div>
                </motion.section>

                {/* Features & Description Section */}
                <motion.section
                  {...fadeInUp}
                  className="bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm"
                >
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <FaCheckCircle className="mr-2 text-teal-600" />
                    Features & Description
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Included Items (one per line)
                      </label>
                      <textarea
                        name="includedItems"
                        value={formData.includedItems}
                        onChange={handleChange}
                        rows="4"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        placeholder="e.g., Welcome Drink&#10;Snacks&#10;Towels"
                      ></textarea>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Signature Meal Details (one per line)
                      </label>
                      <textarea
                        name="signatureMealDetails"
                        value={formData.signatureMealDetails}
                        onChange={handleChange}
                        rows="4"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        placeholder="e.g., Grilled Fish&#10;Greek Salad&#10;Local Wine"
                      ></textarea>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Extra Services (one per line)
                      </label>
                      <textarea
                        name="extraServices"
                        value={formData.extraServices}
                        onChange={handleChange}
                        rows="4"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        placeholder="e.g., DJ Set - €500&#10;Premium Bar - €300"
                      ></textarea>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700">
                        Description
                      </label>
                      <textarea
                        name="description"
                        value={formData.description || ""}
                        onChange={handleChange}
                        rows="4"
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                        placeholder="Detailed description of the yacht..."
                      ></textarea>
                    </div>
                  </div>
                </motion.section>

                {/* Image Upload Section */}
                <motion.section
                  {...fadeInUp}
                  className="bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm"
                >
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <FaImage className="mr-2 text-orange-600" />
                    Yacht Images
                  </h3>
                  {/* Display Current & New Images */}
                  <div className="flex flex-wrap gap-4 mb-4">
                    {imagePreviews.map((previewUrl, index) => {
                      // Determine if this preview corresponds to an existing image or a new file
                      const isExisting = currentImages.includes(previewUrl);
                      const newFileIndex = isExisting
                        ? -1
                        : imagePreviews.filter(
                            (p, i) => i < index && p.startsWith("blob:")
                          ).length; // Calculate index within new files

                      return (
                        <div
                          key={previewUrl || index}
                          className="relative group border rounded-md p-1 shadow-sm"
                        >
                          <img
                            src={previewUrl}
                            alt={`Yacht Preview ${index + 1}`}
                            className="h-24 w-auto rounded"
                          />
                          <button
                            type="button"
                            onClick={() =>
                              handleRemoveImage(
                                isExisting
                                  ? currentImages.indexOf(previewUrl)
                                  : newFileIndex,
                                !isExisting
                              )
                            }
                            className="absolute top-0 right-0 m-1 p-0.5 bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            aria-label="Remove image"
                          >
                            <FaMinusCircle size={16} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  {/* File Input for Adding More Images */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Add More Images
                    </label>
                    <input
                      type="file"
                      name="yachtImages" // Use a name that backend expects for multiple files
                      accept="image/*"
                      multiple // Allow multiple file selection
                      onChange={handleImageChange}
                      className="mt-1 block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                    />
                    <p className="mt-1 text-xs text-gray-500">
                      Upload additional images (JPG, PNG, WEBP, GIF). Max 10MB
                      per file.
                    </p>
                  </div>
                </motion.section>

                {/* Status Section */}
                <motion.section
                  {...fadeInUp}
                  className="bg-gray-50 rounded-lg p-6 border border-gray-200 shadow-sm"
                >
                  <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                    <FaCog className="mr-2 text-gray-600" />
                    Status
                  </h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">
                      Yacht Status
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleChange}
                      className="mt-1 block w-full md:w-1/3 rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                    >
                      {YACHT_STATUSES.map((status) => (
                        <option
                          key={status}
                          value={status}
                          className="capitalize"
                        >
                          {status}
                        </option>
                      ))}
                    </select>
                  </div>
                </motion.section>
              </form>
            </div>

            {/* Footer Actions */}
            <div className="sticky bottom-0 bg-gray-100 px-6 py-4 border-t border-gray-200 flex justify-between items-center rounded-b-xl">
              {/* Delete Button */}
              <button
                type="button"
                onClick={() => onDelete(yacht._id)}
                disabled={isDeleting}
                className="px-5 py-2 text-sm font-medium text-white bg-red-600 hover:bg-blue-700 rounded-full shadow-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors"
              >
                {isDeleting ? (
                  <>
                    <LoadingSpinner size="sm" color="text-white" />
                    <span>Deleting...</span>
                  </>
                ) : (
                  <>
                    <FaTrash />
                    <span>Delete Yacht</span>
                  </>
                )}
              </button>

              {/* Cancel and Save Buttons */}
              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-full shadow-sm hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isUpdating || !hasChanges} // Disable if updating or no changes
                  className={`px-5 py-2 text-sm font-medium text-white ${
                    hasChanges ? 'bg-blue-700 hover:bg-blue-800' : 'bg-gray-400'
                  } rounded-full shadow-sm disabled:opacity-60 disabled:cursor-not-allowed flex items-center space-x-2 transition-colors`}
                >
                  {isUpdating ? (
                    <>
                      <LoadingSpinner size="sm" color="text-white" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <>
                      <FaSave />
                      <span>{hasChanges ? 'Save Changes' : 'No Changes'}</span>
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default YachtDetailsModal;
